import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  ExperimentComparisonView,
  type ComparisonData,
} from "@app/components/ExperimentComparisonView";
import { Checkbox } from "@app/components/ui/checkbox";
import API from "@app/services/api";
import { Trans, useLingui } from "@lingui/macro";

interface Experiment {
  id: string;
  name: string;
  status: string;
  avg_score: number | null;
  created_at: string;
}

export default function ExperimentComparePage() {
  const { t } = useLingui();
  const { id: datasetId } = useParams();
  const [searchParams] = useSearchParams();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExperiments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when datasetId changes
  }, [datasetId]);

  useEffect(() => {
    // Pre-select from URL params
    const ids = searchParams.get("ids");
    if (ids) {
      setSelectedIds(ids.split(","));
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedIds.length >= 2) {
      loadComparison();
    } else {
      setComparisonData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when selectedIds change
  }, [selectedIds]);

  const loadExperiments = async () => {
    try {
      const response = await API.get({
        path: `/dataset/${datasetId}/experiments`,
        query: { limit: 100 },
      });
      if (response.ok) {
        setExperiments(response.data.experiments || []);
      }
    } catch (e) {
      console.error("Error loading experiments:", e);
    }
  };

  const loadComparison = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.get({
        path: `/dataset/${datasetId}/experiments/compare`,
        query: { experiment_ids: selectedIds.join(",") },
      });
      if (response.ok) {
        setComparisonData(response.data.comparison);
      } else {
        setError(response.error || t`Erreur lors de la comparaison`);
      }
    } catch {
      setError(t`Erreur lors de la comparaison`);
    } finally {
      setLoading(false);
    }
  };

  const toggleExperiment = (expId: string) => {
    setSelectedIds((prev) =>
      prev.includes(expId)
        ? prev.filter((id) => id !== expId)
        : [...prev, expId],
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/datasets/${datasetId}`}
          className="text-sm text-primary hover:underline"
        >
          <Trans>← Retour au jeu de données</Trans>
        </Link>
        <h1 className="text-3xl font-bold mt-2"><Trans>Comparer les expériences</Trans></h1>
        <p className="text-slate-600 mt-1">
          <Trans>Sélectionnez au moins 2 expériences pour les comparer côte à côte.</Trans>
        </p>
      </div>

      {/* Experiment selector */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            <Trans>Sélectionner les expériences ({selectedIds.length} sélectionnées)</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {experiments.length === 0 ? (
            <p className="text-slate-500 text-sm">
              <Trans>Aucune expérience disponible.</Trans>
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {experiments.map((exp) => (
                <label
                  key={exp.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedIds.includes(exp.id)
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 hover:border-primary/50"
                  }`}
                >
                  <Checkbox
                    checked={selectedIds.includes(exp.id)}
                    onCheckedChange={() => toggleExperiment(exp.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{exp.name}</p>
                    <p className="text-xs text-slate-500">
                      {exp.status} •{" "}
                      {exp.avg_score !== null
                        ? `Score: ${(exp.avg_score * 100).toFixed(0)}%`
                        : t`Pas de score`}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comparison results */}
      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded text-sm mb-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-8 text-slate-500">
          <Trans>Chargement de la comparaison...</Trans>
        </div>
      )}

      {selectedIds.length < 2 && !loading && (
        <div className="text-center py-12 text-slate-500">
          <p className="text-lg"><Trans>Sélectionnez au moins 2 expériences</Trans></p>
          <p className="text-sm mt-1">
            <Trans>pour voir la comparaison côte à côte avec les graphiques.</Trans>
          </p>
        </div>
      )}

      {comparisonData && !loading && (
        <ExperimentComparisonView data={comparisonData} />
      )}
    </div>
  );
}
